// LAYOUT-SPEC §0 — one deliverable date (wireframe 1)
//
//   ONE QUESTION  Is this date owed, what was sent for it, and what did the
//                 government say?
//   PRIMARY OBJECT  One occurrence (Archetype C, detail side panel).
//   PRIMARY VERB    + Submit. Record review is the government's verdict and a
//                 separate grant (area:execution.verdict); moving, waiving,
//                 not-owed (a generated date) and removing (a date added by
//                 hand) live behind [...], and a waiver or not-owed is dual
//                 control (D111): one person proposes, another approves.
//   WORST-CASE VOLUME  A handful of submissions, each with a few files and
//                 up to five reviews.
//
// The drawer stores only the occurrence id (the ?occ= param) and reads the
// row from GetOccurrence keyed on it. It never copies the row into state.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraBadge, SpecraButton, SpecraDateField, SpecraEmptyState, SpecraFileRow, SpecraFormField, SpecraInlineBanner, SpecraKeyValueRow, SpecraLink, SpecraOverlayFooter, SpecraRecordGroups, SpecraSectionHeader, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ExternalLink, FileText, Link2, MoreHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAuth } from '../../../../contexts/authContext';
import { useArtifactNames } from '../../../../hooks/useDeliveryDocuments';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { DocumentPreviewDialog } from '../../_shared/components/DocumentPreviewDialog';
import { useCmAccess } from '../access';
import { useCmDrawerParams } from '../drawerParams';
import { useApproveDecision, useDeliverableSettings, useDuty, useMoveOccurrence, useOccurrence, useProposeNotOwed, useProposeWaiver, useRejectDecision, useRemoveOccurrence, useWorkstream } from '../hooks';
import { cmDate, cmDateLong, cmMonth, cmSpan, instantDay, todayISO } from '../lib/format';
import { describeRule } from '../lib/rule';
import { HANDLING_LABEL, NOT_OWED_REASON_LABEL, occurrenceStatus, tierLabel } from '../lib/status';
import { useCmLookups, useUserNames } from '../lookups';
import { DENY_REASON } from './AwaitingApproval';
import { CmDrawer } from './CmDrawer';
import { CmSourceButton, CmStatusBadge } from './common';
const ROLL_NOTE = {
    following_federal_workday: 'a weekend or federal holiday rolls to the next workday',
    preceding_federal_workday: 'a weekend or federal holiday rolls to the workday before',
    none: 'no weekend or holiday roll'
};
const LINK_KIND_LABEL = {
    sharepoint_file: 'SharePoint',
    sharepoint_folder: 'SharePoint folder',
    sharepoint: 'SharePoint',
    powerbi: 'Power BI',
    servicenow: 'ServiceNow',
    confluence: 'Confluence'
};
function linkHost(url) {
    try {
        return new URL(url).hostname;
    } catch  {
        return 'Link';
    }
}
export function attachmentKindLabel(a) {
    if (!a.url) return 'File';
    return LINK_KIND_LABEL[a.linkKind] ?? linkHost(a.url);
}
const DECISION_WORD = {
    approved: 'Approved',
    rejected: 'Rejected',
    comments: 'Comments',
    auto_approved: 'Approved (review window lapsed)'
};
function isNotFound(err) {
    return err instanceof ConnectError && err.code === Code.NotFound;
}
export function OccurrenceDrawer({ programId, occId }) {
    const params = useCmDrawerParams();
    const q = useOccurrence(programId, occId);
    const occ = q.data?.occurrence;
    const duty = useDuty(programId, occ?.dutyId);
    const team = useWorkstream(programId, occ?.workstreamId || undefined);
    const lk = useCmLookups(programId);
    const access = useCmAccess(programId);
    const names = useUserNames();
    const settings = useDeliverableSettings(programId);
    const { user } = useAuth();
    const [mode, setMode] = useState({
        kind: 'view'
    });
    const [preview, setPreview] = useState(null);
    const [historyOpen, setHistoryOpen] = useState([]);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const close = ()=>params.close('occ');
    const remove = useRemoveOccurrence(programId, {
        success: 'Date removed',
        failure: 'Could not remove'
    });
    const submissions = useMemo(()=>[
            ...q.data?.submissions ?? []
        ].sort((a, b)=>b.attempt - a.attempt), [
        q.data?.submissions
    ]);
    const decisions = q.data?.decisions ?? [];
    const open = decisions.find((d)=>d.state === 'UnderReview');
    const latestLive = submissions.find((s)=>!s.withdrawn);
    const artifactNames = useArtifactNames(useMemo(()=>submissions.flatMap((s)=>s.attachments.map((a)=>a.artifactId)).filter(Boolean), [
        submissions
    ]));
    const d = duty.data?.duty ?? (occ ? lk.dutyById.get(occ.dutyId) : undefined);
    const rules = [
        ...duty.data?.rules ?? [],
        ...(duty.data?.assignments ?? []).flatMap((a)=>a.teamRules)
    ];
    const rule = occ?.ruleId ? rules.find((r)=>r.id === occ.ruleId) : undefined;
    const status = occ ? occurrenceStatus(occ, d?.finalReviewTier) : undefined;
    if (q.error) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Deliverable date",
            onClose: close,
            testId: Testids.cmOccurrence.drawer,
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.cmOccurrence.notFound,
                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    title: isNotFound(q.error) ? 'This date no longer exists' : 'Could not load this date',
                    description: isNotFound(q.error) ? 'It may have been retired by a schedule change, or the link is from another program.' : apiErrorMessage(q.error, 'Try again in a moment.'),
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: close,
                        children: "Close"
                    })
                })
            })
        });
    }
    if (!occ || !status) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Deliverable date",
            onClose: close,
            testId: Testids.cmOccurrence.drawer,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                tone: "muted",
                size: "caption",
                children: "Loading…"
            })
        });
    }
    const exercise = lk.exerciseById.get(occ.exerciseId);
    const ot = exercise ? lk.optionalTaskById.get(exercise.optionalTaskId) : undefined;
    const period = lk.periodById.get(occ.periodOfPerformanceId);
    // The SOW's handling of this deliverable for the date's team: the team rule
    // that minted it, else the team's in-force SOW's assignment.
    const assignments = duty.data?.assignments ?? [];
    const teamSowIds = new Set((team.data?.sows ?? []).filter((x)=>x.state === 'in_force').map((x)=>x.id));
    const teamAssignment = assignments.find((a)=>a.teamRules.some((r)=>r.id === occ.ruleId)) ?? assignments.find((a)=>teamSowIds.has(a.sowId));
    const owedBecause = exercise ? `${ot?.short ? `${ot.short} ` : ''}exercise ${exercise.clinNumber || ''} (PoP ${cmSpan(exercise.start, exercise.end)})` : period ? `${period.name} (${cmSpan(period.start, period.end)})` : occ.ruleId ? 'The contract period this rule covers' : `Added by hand${occ.createdByUserId ? ` by ${names(occ.createdByUserId)}` : ''}`;
    const roll = settings.data?.roll ? ROLL_NOTE[settings.data.roll] : undefined;
    const closedState = [
        'not_owed',
        'retired'
    ].includes(status.state);
    const waivedOrClosed = closedState || status.state === 'waived';
    const isMine = (userId)=>!!user?.id && userId === user.id;
    // A date added by hand is removed; a generated one is proposed not owed,
    // since the contract minted it. Neither while it has a live filing or an
    // open decision (the server refuses both).
    const addedByHand = !occ.ruleId;
    const title = d?.title || 'Deliverable';
    const periodLabel = occ.label || cmMonth(occ.nominalDueOn || occ.dueOn);
    // Hidden entirely for a read-only viewer; a verdict-only viewer sees only Record review.
    const headerActions = mode.kind === 'view' && (access.canWrite || access.canVerdict) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            access.canWrite && !closedState ? /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: ()=>params.open('submit', occ.id),
                "data-testid": Testids.cmOccurrence.submit,
                children: "+ Submit"
            }) : null,
            access.canVerdict && latestLive ? /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                onClick: ()=>params.open('review', latestLive.id),
                "data-testid": Testids.cmOccurrence.recordReview,
                children: "Record review"
            }) : null,
            access.canWrite && !closedState ? /*#__PURE__*/ _jsxs(DropdownMenu, {
                children: [
                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "ghost",
                            "aria-label": "More actions",
                            "data-testid": Testids.cmOccurrence.more,
                            children: /*#__PURE__*/ _jsx(MoreHorizontal, {
                                className: "h-4 w-4",
                                "aria-hidden": true
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                        align: "end",
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setMode({
                                        kind: 'move'
                                    }),
                                "data-testid": Testids.cmOccurrence.move,
                                children: "Move due date"
                            }),
                            !open && !waivedOrClosed ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                        onSelect: ()=>setMode({
                                                kind: 'waiver'
                                            }),
                                        "data-testid": Testids.cmOccurrence.proposeWaiver,
                                        children: "Propose waiver"
                                    }),
                                    addedByHand ? null : /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                        onSelect: ()=>setMode({
                                                kind: 'notOwed'
                                            }),
                                        "data-testid": Testids.cmOccurrence.proposeNotOwed,
                                        children: "Propose not owed"
                                    })
                                ]
                            }) : null,
                            addedByHand && !open && !latestLive ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setConfirmRemove(true),
                                "data-testid": Testids.cmOccurrence.remove,
                                children: "Remove date"
                            }) : null
                        ]
                    })
                ]
            }) : null
        ]
    }) : undefined;
    const origin = `${d?.origin === 'sow' ? 'SOW deliverable' : d?.origin === 'manual' ? 'Added by hand' : 'Award'}${d?.pwsRef ? ` PWS ${d.pwsRef}` : ''}${d?.letter ? ` deliverable ${d.letter}` : ''}${d?.sourceLocator ? ` · ${d.sourceLocator}` : ''}`;
    return /*#__PURE__*/ _jsxs(CmDrawer, {
        title: /*#__PURE__*/ _jsx("span", {
            "data-testid": Testids.cmOccurrence.title,
            children: title
        }),
        description: periodLabel,
        eyebrow: /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex flex-wrap items-center gap-2",
            children: [
                origin,
                /*#__PURE__*/ _jsx(CmSourceButton, {
                    locator: d?.sourceLocator
                })
            ]
        }),
        headerActions: headerActions,
        onClose: close,
        testId: Testids.cmOccurrence.drawer,
        footer: mode.kind === 'waiver' || mode.kind === 'notOwed' ? /*#__PURE__*/ _jsx(ProposeForm, {
            programId: programId,
            occurrenceId: occ.id,
            kind: mode.kind,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'move' ? /*#__PURE__*/ _jsx(MoveForm, {
            programId: programId,
            occurrenceId: occ.id,
            currentDue: occ.dueOn,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : mode.kind === 'end' ? /*#__PURE__*/ _jsx(EndDecisionForm, {
            programId: programId,
            decision: mode.decision,
            withdraw: mode.withdraw,
            onDone: ()=>setMode({
                    kind: 'view'
                })
        }) : null,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Status",
                                value: /*#__PURE__*/ _jsx(CmStatusBadge, {
                                    status: status,
                                    testId: Testids.cmOccurrence.status
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Team",
                                value: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        lk.teamName(occ.workstreamId),
                                        teamAssignment ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                ' ',
                                                "· ",
                                                HANDLING_LABEL[teamAssignment.handling] ?? teamAssignment.handling,
                                                " here",
                                                teamAssignment.teamRules.some((r)=>r.id === occ.ruleId) ? ' (team rule)' : ''
                                            ]
                                        }) : null
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                "data-testid": Testids.cmOccurrence.due,
                                label: "Due",
                                value: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium text-foreground",
                                            children: cmDateLong(occ.dueOn, 'date not stated')
                                        }),
                                        rule ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                ' ',
                                                "(rule: ",
                                                describeRule(rule),
                                                roll ? `; ${roll}` : '',
                                                ")"
                                            ]
                                        }) : null,
                                        occ.movedFromDueOn ? /*#__PURE__*/ _jsxs("span", {
                                            className: "block",
                                            children: [
                                                "Moved from ",
                                                cmDate(occ.movedFromDueOn),
                                                ": ",
                                                occ.movedReason
                                            ]
                                        }) : null
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                "data-testid": Testids.cmOccurrence.owedBecause,
                                label: "Owed because",
                                value: owedBecause
                            }),
                            occ.waivedOn || occ.waiverReason ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                "data-testid": Testids.cmOccurrence.waiverNote,
                                label: "Waived",
                                value: `By the COR on ${occ.waivedOn ? cmDate(occ.waivedOn) : 'a date not recorded'}${occ.waiverReason ? `: “${occ.waiverReason}”` : ''}`
                            }) : null
                        ]
                    }),
                    open ? /*#__PURE__*/ _jsx(PendingDecisionBanner, {
                        decision: open,
                        proposer: names(open.proposedByUserId),
                        mine: isMine(open.proposedByUserId),
                        canAct: access.canWrite && mode.kind === 'view',
                        programId: programId,
                        onEnd: (withdraw)=>setMode({
                                kind: 'end',
                                decision: open,
                                withdraw
                            })
                    }) : null,
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "Submissions"
                            }),
                            submissions.length === 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: [
                                    "Nothing submitted for this date yet.",
                                    access.canWrite && !closedState ? ' Use + Submit when it goes out.' : ''
                                ]
                            }) : /*#__PURE__*/ _jsx("ol", {
                                className: "space-y-4",
                                children: submissions.map((s)=>/*#__PURE__*/ _jsx(SubmissionItem, {
                                        s: s,
                                        latest: s.id === latestLive?.id,
                                        statusLabel: s.id === latestLive?.id ? status.label : undefined,
                                        artifactNames: artifactNames,
                                        onPreview: setPreview
                                    }, s.id))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraRecordGroups, {
                        asDetails: true,
                        variant: "hairline",
                        openIds: historyOpen,
                        onOpenChange: setHistoryOpen,
                        getGroupTestId: ()=>Testids.cmOccurrence.history,
                        groups: [
                            {
                                id: 'history',
                                header: {
                                    title: 'History'
                                },
                                content: /*#__PURE__*/ _jsx("ul", {
                                    className: "space-y-1 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                    children: historyLines(occ, decisions, names, rule ? describeRule(rule) : '').map((line, i)=>// Two history lines can read the same (a waiver proposed twice).
                                        /*#__PURE__*/ _jsx("li", {
                                            "data-testid": Testids.cmOccurrence.historyRow,
                                            children: line
                                        }, `${i}-${line}`))
                                })
                            }
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: preview?.artifactId ?? '',
                title: preview?.name ?? 'Submission file',
                open: !!preview,
                onOpenChange: (o)=>{
                    if (!o) setPreview(null);
                }
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmRemove,
                onOpenChange: setConfirmRemove,
                title: "Remove this date?",
                description: "It was added by hand, so it is removed outright rather than marked not owed.",
                isPending: remove.isPending,
                onConfirm: ()=>remove.mutate({
                        occurrenceId: occ.id
                    }, {
                        onSuccess: close
                    })
            })
        ]
    });
}
function historyLines(occ, decisions, names, ruleWords) {
    const out = [];
    out.push(occ.ruleId ? `Scheduled by the rule${ruleWords ? `: ${ruleWords}` : ''}` : `Added by hand${occ.createdByUserId ? ` by ${names(occ.createdByUserId)}` : ''}`);
    if (occ.movedFromDueOn) out.push(`Due date moved from ${cmDate(occ.movedFromDueOn)} to ${cmDate(occ.dueOn)}: ${occ.movedReason}`);
    for (const d of [
        ...decisions
    ].reverse()){
        const what = d.kind === 'waiver' ? 'Waiver' : 'Not-owed proposal';
        const why = d.kind === 'waiver' ? d.reason : d.note || d.reason;
        out.push(`${d.kind === 'waiver' ? 'Waiver proposed' : 'Proposed as not owed'} by ${names(d.proposedByUserId)} on ${cmDate(instantDay(d.proposedAt))}${why ? `: “${why}”` : ''}`);
        const on = d.decidedAt ? ` on ${cmDate(instantDay(d.decidedAt))}` : '';
        if (d.state === 'Applied') out.push(`${what} approved by ${names(d.decidedByUserId || d.approvedByUserId)}${on}`);
        if (d.state === 'Rejected') out.push(`${what} rejected by ${names(d.decidedByUserId)}${on}: “${d.stateReason}”`);
        if (d.state === 'Cancelled') out.push(`${what} withdrawn by ${names(d.decidedByUserId)}${on}: “${d.stateReason}”`);
    }
    if (occ.retired) out.push(`Retired: ${occ.retiredReason.replace(/_/g, ' ')}`);
    return out;
}
function PendingDecisionBanner({ decision, proposer, mine, canAct, programId, onEnd }) {
    const approve = useApproveDecision(programId, {
        success: 'Approved'
    });
    const deny = useRejectDecision(programId, {
        success: 'Proposal denied'
    });
    const deciding = approve.isPending || deny.isPending;
    const what = decision.kind === 'waiver' ? 'Waiver' : 'Not owed';
    const why = decision.kind === 'waiver' ? decision.reason : decision.note || decision.reason;
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "warning",
        variant: "framed",
        "data-testid": Testids.cmOccurrence.pendingDecision,
        action: canAct ? mine ? /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            variant: "secondary",
            onClick: ()=>onEnd(true),
            "data-testid": Testids.cmOccurrence.withdrawDecision,
            children: "Withdraw"
        }) : /*#__PURE__*/ _jsxs("div", {
            className: "flex gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    isLoading: approve.isPending,
                    disabled: deciding,
                    onClick: ()=>approve.mutate({
                            changeSetId: decision.changeSetId
                        }),
                    "data-testid": Testids.cmOccurrence.approveDecision,
                    children: "Approve"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "secondary",
                    isLoading: deny.isPending,
                    disabled: deciding,
                    onClick: ()=>deny.mutate({
                            changeSetId: decision.changeSetId,
                            reason: DENY_REASON
                        }),
                    "data-testid": Testids.cmOccurrence.rejectDecision,
                    children: "Deny"
                })
            ]
        }) : undefined,
        children: [
            what,
            " proposed by ",
            proposer,
            " on ",
            cmDate(instantDay(decision.proposedAt)),
            why ? `: “${why}”` : '',
            ".",
            ' ',
            mine ? 'Someone else must approve it; you can withdraw it.' : 'A second person must approve it before it takes effect.'
        ]
    });
}
function SubmissionItem({ s, latest, statusLabel, artifactNames, onPreview }) {
    return /*#__PURE__*/ _jsxs("li", {
        className: s.withdrawn ? 'opacity-60' : '',
        "data-testid": Testids.cmOccurrence.submissionRow,
        "data-row-id": s.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-baseline gap-2 text-[length:var(--fs-ui-body)]",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-medium",
                        "data-testid": Testids.cmOccurrence.attempt,
                        children: [
                            "#",
                            s.attempt
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Submitted ",
                            cmDate(s.submittedOn, 'on an unstated day'),
                            s.submittedByDisplay ? ` by ${s.submittedByDisplay}` : ''
                        ]
                    }),
                    s.withdrawn ? /*#__PURE__*/ _jsxs("span", {
                        className: "text-muted-foreground",
                        children: [
                            "Withdrawn: ",
                            s.withdrawnReason
                        ]
                    }) : null,
                    latest && statusLabel ? /*#__PURE__*/ _jsx("span", {
                        className: "ml-auto text-[length:var(--fs-ui-meta)] text-muted-foreground",
                        children: statusLabel
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "mt-1.5 space-y-1",
                children: s.attachments.map((a)=>{
                    const name = a.url ? a.label || a.url : artifactNames.get(a.artifactId) || a.label || 'File';
                    return /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsx(SpecraFileRow, {
                            "data-testid": Testids.cmOccurrence.attachment,
                            icon: a.url ? /*#__PURE__*/ _jsx(Link2, {
                                className: "h-3.5 w-3.5",
                                "aria-hidden": true
                            }) : /*#__PURE__*/ _jsx(FileText, {
                                className: "h-3.5 w-3.5",
                                "aria-hidden": true
                            }),
                            name: name,
                            meta: attachmentKindLabel(a),
                            actions: a.url ? /*#__PURE__*/ _jsxs(SpecraLink, {
                                href: a.url,
                                target: "_blank",
                                rel: "noreferrer noopener",
                                className: "inline-flex items-center gap-1",
                                children: [
                                    "Open ",
                                    /*#__PURE__*/ _jsx(ExternalLink, {
                                        className: "h-3 w-3",
                                        "aria-hidden": true
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                onClick: ()=>onPreview({
                                        artifactId: a.artifactId,
                                        name
                                    }),
                                children: "Open"
                            })
                        })
                    }, a.id);
                })
            }),
            s.note ? /*#__PURE__*/ _jsxs("p", {
                className: "mt-1 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "Note: “",
                    s.note,
                    "”"
                ]
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                className: "mt-1.5 text-[length:var(--fs-ui-caption)]",
                children: s.reviews.length === 0 ? /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "Reviews: none yet"
                }) : /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-1",
                    children: s.reviews.map((r)=>/*#__PURE__*/ _jsxs("li", {
                            className: r.superseded ? 'text-muted-foreground line-through' : '',
                            "data-testid": Testids.cmOccurrence.reviewRow,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: r.superseded ? 'neutral' : r.decision === 'rejected' ? 'danger' : r.decision === 'approved' || r.decision === 'auto_approved' ? 'success' : 'neutral',
                                    children: DECISION_WORD[r.decision] ?? r.decision
                                }),
                                ' ',
                                r.reviewerDisplay || 'Reviewer not named',
                                " · ",
                                tierLabel(r.tier),
                                " ·",
                                ' ',
                                cmDate(r.decidedOn),
                                r.notes ? /*#__PURE__*/ _jsxs("div", {
                                    className: "pl-3 text-muted-foreground",
                                    children: [
                                        "“",
                                        r.notes,
                                        "”"
                                    ]
                                }) : null
                            ]
                        }, r.id))
                })
            })
        ]
    });
}
function FormShell({ title, children, error, confirmLabel, confirmDisabled, loading, onConfirm, onCancel, confirmTestId }) {
    return /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
        tray: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    className: "font-medium",
                    children: title
                }),
                children,
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(error, 'Could not save')
                }) : null
            ]
        }),
        onCancel: onCancel,
        onConfirm: onConfirm,
        confirmLabel: confirmLabel,
        confirmDisabled: confirmDisabled,
        loading: loading,
        cancelProps: {
            variant: 'ghost'
        },
        confirmProps: {
            'data-testid': confirmTestId
        }
    });
}
/** D98: a date field never defaults to today; it offers today as a suggestion. */ export function UseTodayButton({ onUse, testId }) {
    return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "link",
        size: "inline",
        onClick: ()=>onUse(todayISO()),
        "data-testid": testId,
        children: "Use today’s date"
    });
}
function ProposeForm({ programId, occurrenceId, kind, onDone }) {
    const waiver = useProposeWaiver(programId, {
        errors: 'inline',
        success: 'Waiver proposed. A second person must approve it.'
    });
    const notOwed = useProposeNotOwed(programId, {
        errors: 'inline',
        success: 'Proposed as not owed. A second person must approve it.'
    });
    const [waivedOn, setWaivedOn] = useState('');
    const [reason, setReason] = useState('');
    const [notOwedReason, setNotOwedReason] = useState('not_owed');
    const m = kind === 'waiver' ? waiver : notOwed;
    // The COR's date is optional; the reason never is.
    const ready = reason.trim() !== '';
    const submit = ()=>{
        const done = {
            onSuccess: onDone
        };
        if (kind === 'waiver') waiver.mutate({
            occurrenceId,
            waivedOn,
            reason: reason.trim()
        }, done);
        else notOwed.mutate({
            occurrenceId,
            reason: notOwedReason,
            note: reason.trim()
        }, done);
    };
    return /*#__PURE__*/ _jsx(FormShell, {
        title: kind === 'waiver' ? 'Propose a waiver of this date' : 'Propose this date as not owed',
        error: m.error,
        confirmLabel: "Propose",
        confirmDisabled: !ready,
        loading: m.isPending,
        onConfirm: submit,
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.proposeSave,
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.cmOccurrence.proposeDrawer,
            className: "space-y-3",
            children: [
                kind === 'waiver' ? /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Waived by the COR on",
                    value: waivedOn,
                    onValueChange: setWaivedOn,
                    hint: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Optional; leave blank if the date was not recorded.",
                            ' ',
                            /*#__PURE__*/ _jsx(UseTodayButton, {
                                onUse: setWaivedOn
                            })
                        ]
                    }),
                    "data-testid": Testids.cmOccurrence.proposeWaivedOn
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why it is not owed",
                    required: true,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: notOwedReason,
                        onChange: (e)=>setNotOwedReason(e.target.value),
                        "data-testid": Testids.cmOccurrence.proposeNotOwedReason,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "not_owed",
                                children: "The contract does not require it"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: "generated_in_error",
                                children: "This date was created in error"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: kind === 'waiver' ? 'The COR’s reason' : 'Explain, in a sentence',
                    required: true,
                    hint: "A second person reads this before approving.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        "data-testid": Testids.cmOccurrence.proposeReason
                    })
                })
            ]
        })
    });
}
function MoveForm({ programId, occurrenceId, currentDue, onDone }) {
    const move = useMoveOccurrence(programId, {
        errors: 'inline',
        success: 'Due date moved'
    });
    const [dueOn, setDueOn] = useState('');
    const [reason, setReason] = useState('');
    const ready = dueOn !== '' && dueOn !== currentDue && reason.trim() !== '';
    return /*#__PURE__*/ _jsx(FormShell, {
        title: "Move the due date",
        error: move.error,
        confirmLabel: "Move",
        confirmDisabled: !ready,
        loading: move.isPending,
        onConfirm: ()=>move.mutate({
                occurrenceId,
                dueOn,
                reason: reason.trim()
            }, {
                onSuccess: onDone
            }),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.moveSave,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            "data-testid": Testids.cmOccurrence.moveDrawer,
            children: [
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "New due date",
                    required: true,
                    value: dueOn,
                    onValueChange: setDueOn,
                    hint: currentDue ? `Currently ${cmDate(currentDue)}` : 'No due date stated today',
                    "data-testid": Testids.cmOccurrence.moveDueOn
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why it moved",
                    required: true,
                    hint: "e.g. “COR email of 3 Mar extended it”",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        "data-testid": Testids.cmOccurrence.moveReason
                    })
                })
            ]
        })
    });
}
function EndDecisionForm({ programId, decision, withdraw, onDone }) {
    const reject = useRejectDecision(programId, {
        errors: 'inline',
        success: withdraw ? 'Proposal withdrawn' : 'Proposal rejected'
    });
    const [reason, setReason] = useState('');
    return /*#__PURE__*/ _jsx(FormShell, {
        title: withdraw ? 'Withdraw your proposal' : 'Reject this proposal',
        error: reject.error,
        confirmLabel: withdraw ? 'Withdraw' : 'Reject',
        confirmDisabled: reason.trim() === '',
        loading: reject.isPending,
        onConfirm: ()=>reject.mutate({
                changeSetId: decision.changeSetId,
                reason: reason.trim()
            }, {
                onSuccess: onDone
            }),
        onCancel: onDone,
        confirmTestId: Testids.cmOccurrence.decisionConfirm,
        children: /*#__PURE__*/ _jsx(SpecraFormField, {
            label: "Reason",
            required: true,
            hint: withdraw ? 'Kept in the history. The date is unchanged.' : 'The proposer sees this. The date is unchanged and can be proposed again.',
            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                rows: 2,
                value: reason,
                onChange: (e)=>setReason(e.target.value),
                "data-testid": Testids.cmOccurrence.decisionReason
            })
        })
    });
}
export { NOT_OWED_REASON_LABEL };
